/**
 * Backend-backed: GET /api/pairs, GET /api/metrics/pairs/:agent/:dataset,
 * reference CRUD, POST /api/metrics/aggregate, POST /api/pairs/connect (nightly).
 */
import React from "react";
import styles from "../components/Dashboard.module.css";
import {
  metricsApi,
  MetricReferencePayload,
  MetricReferenceThreshold,
  MonitoredPair,
  PairMetricsPayload,
} from "../services/metricsApi";

type ReferenceDraftRow = {
  value: string;
  enabled: boolean;
};

const LATEST_WINDOW = "latest";

const emptyReference: MetricReferencePayload = {
  agentId: "",
  datasetId: "",
  thresholds: [],
  updatedAt: "",
  updatedBy: "",
};

const referenceKey = (metric: string, window = LATEST_WINDOW) => `${metric}\0${window}`;

const toReferenceDraft = (
  metrics: PairMetricsPayload["liveMetrics"],
  reference: MetricReferencePayload,
): Record<string, ReferenceDraftRow> => {
  const byMetric = new Map(
    reference.thresholds.map((threshold) => [referenceKey(threshold.metric, threshold.window), threshold]),
  );

  return Object.fromEntries(
    metrics.map((metric) => {
      const threshold = byMetric.get(referenceKey(metric.metricName, LATEST_WINDOW));
      return [
        referenceKey(metric.metricName, LATEST_WINDOW),
        {
          value: threshold ? String(threshold.value) : "",
          enabled: threshold?.enabled ?? false,
        },
      ];
    }),
  );
};

const buildReferenceThresholds = (
  metrics: PairMetricsPayload["liveMetrics"],
  reference: MetricReferencePayload,
  draft: Record<string, ReferenceDraftRow>,
): MetricReferenceThreshold[] => {
  const visibleMetrics = new Set(metrics.map((metric) => metric.metricName));
  const hiddenThresholds = reference.thresholds.filter(
    (threshold) => threshold.window !== LATEST_WINDOW || !visibleMetrics.has(threshold.metric),
  );
  const visibleThresholds = metrics.flatMap((metric) => {
    const row = draft[referenceKey(metric.metricName, LATEST_WINDOW)];
    const rawValue = row?.value.trim() ?? "";
    if (!rawValue) return [];
    const value = Number(rawValue);
    if (!Number.isFinite(value) || value < 0) {
      throw new Error("Reference value must be a non-negative number.");
    }
    return [
      {
        metric: metric.metricName,
        window: LATEST_WINDOW,
        value,
        enabled: row?.enabled ?? true,
      },
    ];
  });

  return [...hiddenThresholds, ...visibleThresholds];
};

const MetricsPage: React.FC = () => {
  const [pairs, setPairs] = React.useState<MonitoredPair[]>([]);
  const [selectedPairId, setSelectedPairId] = React.useState<string>("");
  const [payload, setPayload] = React.useState<PairMetricsPayload>({ nightly: false, liveMetrics: [] });
  const [reference, setReference] = React.useState<MetricReferencePayload>(emptyReference);
  const [referenceDraft, setReferenceDraft] = React.useState<Record<string, ReferenceDraftRow>>({});
  const [nightlyDraft, setNightlyDraft] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [savingReference, setSavingReference] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<string[]>([]);
  const referenceSaveSeq = React.useRef(0);

  React.useEffect(() => {
    let mounted = true;
    const loadPairs = async () => {
      setErrors([]);
      setLoading(true);
      try {
        const rows = await metricsApi.listPairs();
        if (!mounted) return;
        setPairs(rows);
        const first = rows[0]?.id || "";
        setSelectedPairId(first);
        if (rows.length === 0) setLoading(false);
      } catch {
        if (!mounted) return;
        setLoading(false);
        setErrors(["Failed to load metrics."]);
      }
    };
    void loadPairs();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    let mounted = true;
    if (!selectedPairId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [data, nextReference] = await Promise.all([
          metricsApi.getPairMetrics(selectedPairId),
          metricsApi.getPairReference(selectedPairId),
        ]);
        if (!mounted) return;
        setPayload(data);
        setReference(nextReference);
        setReferenceDraft(toReferenceDraft(data.liveMetrics, nextReference));
        setNightlyDraft(data.nightly);
      } catch {
        if (!mounted) return;
        setErrors(["Failed to load pair metrics."]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [selectedPairId]);

  const dirtyNightly = nightlyDraft !== payload.nightly;

  const handleRefreshMetrics = async () => {
    if (!selectedPairId) return;
    setErrors([]);
    setFeedback(null);
    setRefreshing(true);
    try {
      const next = await metricsApi.refreshPairMetrics(selectedPairId);
      setPayload(next);
      setReferenceDraft(toReferenceDraft(next.liveMetrics, reference));
      setNightlyDraft(next.nightly);
      if (next.refreshOutcome && next.refreshOutcome !== "written") {
        setErrors([next.refreshMessage ?? "Metrics aggregation did not write new values."]);
      } else {
        setFeedback(next.refreshMessage ?? "Metrics refreshed from Langfuse.");
      }
    } catch {
      setErrors(["Could not refresh Langfuse metrics. Check that the pair has a completed run."]);
    } finally {
      setRefreshing(false);
    }
  };

  const persistReferenceDraft = async (nextDraft: Record<string, ReferenceDraftRow>) => {
    if (!selectedPairId) return;
    const saveSeq = referenceSaveSeq.current + 1;
    referenceSaveSeq.current = saveSeq;
    setErrors([]);
    let nextReference: MetricReferencePayload;
    setSavingReference(true);
    try {
      const thresholds = buildReferenceThresholds(payload.liveMetrics, reference, nextDraft);
      if (thresholds.length === 0) {
        await metricsApi.deletePairReference(selectedPairId);
        nextReference = {
          ...emptyReference,
          agentId: payload.agentId ?? "",
          datasetId: payload.datasetId ?? "",
        };
      } else {
        nextReference = await metricsApi.savePairReference(selectedPairId, thresholds);
      }
      const nextPayload = await metricsApi.getPairMetrics(selectedPairId);
      if (referenceSaveSeq.current !== saveSeq) return;
      setReference(nextReference);
      setReferenceDraft(toReferenceDraft(nextPayload.liveMetrics, nextReference));
      setPayload(nextPayload);
      setNightlyDraft(nextPayload.nightly);
    } catch (err) {
      if (referenceSaveSeq.current === saveSeq) {
        setErrors([err instanceof Error ? err.message : "Could not save reference targets."]);
      }
    } finally {
      if (referenceSaveSeq.current === saveSeq) setSavingReference(false);
    }
  };

  const handleReferenceValueChange = (metricName: string, value: string) => {
    const key = referenceKey(metricName, LATEST_WINDOW);
    setReferenceDraft((draft) => {
      const previous = draft[key] ?? { value: "", enabled: false };
      const nextRow = {
        value,
        enabled: value.trim() ? (previous.value.trim() ? previous.enabled : true) : false,
      };
      return { ...draft, [key]: nextRow };
    });
  };

  const handleReferenceValueBlur = (metricName: string, value: string) => {
    const key = referenceKey(metricName, LATEST_WINDOW);
    const previous = referenceDraft[key] ?? { value: "", enabled: false };
    const nextDraft = {
      ...referenceDraft,
      [key]: {
        value,
        enabled: value.trim() ? (previous.value.trim() ? previous.enabled : true) : false,
      },
    };
    setReferenceDraft(nextDraft);
    void persistReferenceDraft(nextDraft);
  };

  const handleReferenceEnabledChange = (metricName: string, enabled: boolean) => {
    const key = referenceKey(metricName, LATEST_WINDOW);
    const previous = referenceDraft[key] ?? { value: "", enabled: false };
    if (!previous.value.trim()) return;
    const nextDraft = {
      ...referenceDraft,
      [key]: { ...previous, enabled },
    };
    setReferenceDraft(nextDraft);
    void persistReferenceDraft(nextDraft);
  };

  const handleSaveNightly = async () => {
    setErrors([]);
    setFeedback(null);
    setSaving(true);
    try {
      const next = await metricsApi.savePairNightly(selectedPairId, nightlyDraft);
      setPayload(next);
      setNightlyDraft(next.nightly);
      setFeedback("Nightly monitoring flag saved for this pair.");
    } catch {
      setErrors(["Could not save pair settings. Please try again."]);
    } finally {
      setSaving(false);
    }
  };

  if (loading && pairs.length === 0 && errors.length === 0) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading metrics...</p>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.metricsPage}>
      <section className={styles.metricsHeaderCard}>
        <h1 className={styles.metricsTitle}>Pair-scoped metrics</h1>
        <div className={styles.metricsPairRow}>
          <label htmlFor="pairSelect" className={styles.metricsLabel}>
            Agent–dataset pair
          </label>
          <select
            id="pairSelect"
            data-testid="pair-select"
            className={`${styles.metricsSelect} ${styles.metricsSelectArrow}`.trim()}
            value={selectedPairId}
            onChange={(e) => {
              setSelectedPairId(e.target.value);
              setErrors([]);
              setFeedback(null);
            }}
          >
            {pairs.map((pair) => (
              <option key={pair.id} value={pair.id}>
                {pair.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className={styles.metricsCard}>
        <div className={styles.metricsSectionHeader}>
          <div>
            <h2 className={styles.metricsSectionTitle}>Langfuse metrics</h2>
            <p className={styles.metricsSectionHint}>
              {payload.lastRun
                ? `Latest aggregation: ${new Date(payload.lastRun).toLocaleString()}${payload.stale ? " (stale)" : ""}`
                : "No aggregation has run yet for this workspace."}
            </p>
          </div>
          <button
            type="button"
            className={styles.metricsSecondaryBtn}
            onClick={() => void handleRefreshMetrics()}
            disabled={refreshing || !selectedPairId}
          >
            {refreshing ? "Refreshing..." : "Refresh from Langfuse"}
          </button>
        </div>
        <div className={styles.metricsTableWrap}>
          <table className={styles.metricsTable}>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Current value</th>
                <th>Reference value</th>
                <th>Reference enabled</th>
              </tr>
            </thead>
            <tbody>
              {payload.liveMetrics.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    No cached Langfuse metrics for this pair. Run a completed evaluation, then refresh from
                    Langfuse.
                  </td>
                </tr>
              ) : (
                payload.liveMetrics.map((row) => (
                  <tr key={row.id}>
                    <td>{row.metricName}</td>
                    <td>{row.currentValue}</td>
                    <td>
                      <input
                        className={styles.metricsReferenceValueInput}
                        type="number"
                        min="0"
                        step="any"
                        placeholder="None"
                        value={referenceDraft[referenceKey(row.metricName, LATEST_WINDOW)]?.value ?? ""}
                        onChange={(e) => handleReferenceValueChange(row.metricName, e.target.value)}
                        onBlur={(e) => handleReferenceValueBlur(row.metricName, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        aria-label={`Reference value for ${row.metricName}`}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        className={styles.metricsCheckbox}
                        checked={referenceDraft[referenceKey(row.metricName, LATEST_WINDOW)]?.enabled ?? false}
                        disabled={!referenceDraft[referenceKey(row.metricName, LATEST_WINDOW)]?.value.trim()}
                        onChange={(e) => handleReferenceEnabledChange(row.metricName, e.target.checked)}
                        aria-label={`Enable reference for ${row.metricName}`}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {savingReference ? <p className={styles.metricsSectionHint}>Saving reference targets...</p> : null}
      </section>

      <section className={styles.metricsCard}>
        <h2 className={styles.metricsSectionTitle}>Nightly monitoring</h2>
        <label className={styles.metricsNightlyRow}>
          <input
            type="checkbox"
            className={styles.metricsCheckbox}
            checked={nightlyDraft}
            onChange={(e) => setNightlyDraft(e.target.checked)}
          />
          <span>Enable nightly monitoring for this pair</span>
        </label>
        <button
          type="button"
          className={styles.metricsPrimaryBtn}
          onClick={() => void handleSaveNightly()}
          disabled={saving || !dirtyNightly || !selectedPairId}
        >
          {saving ? "Saving..." : "Save nightly flag"}
        </button>
      </section>

      {errors.length > 0 ? (
        <section className={styles.metricsErrorBox}>
          {errors.map((err) => (
            <p key={err}>{err}</p>
          ))}
        </section>
      ) : null}

      {feedback ? <section className={styles.metricsSuccessBox}>{feedback}</section> : null}
    </div>
  );
};

export default MetricsPage;
